import { useState, useEffect, useCallback } from 'react';
import { ReportsStorageService, GeneratedReport } from '../services/storage/ReportsStorageService';
import type { FinancialStatementType } from '../types/reports';

export function useSavedReports() {
  const [savedReports, setSavedReports] = useState<GeneratedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Charger tous les rapports sauvegardés
  const loadReports = useCallback(async () => {
    try {
      console.log('📋 useSavedReports: Loading reports...');
      setLoading(true);
      setError(null);
      const reports = await ReportsStorageService.getAllReports();
      console.log('📋 useSavedReports: Loaded reports:', { count: reports.length });
      setSavedReports(reports);
    } catch (err) {
      console.error('❌ useSavedReports: Error loading saved reports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, []);

  // Sauvegarder un nouveau rapport
  const saveReport = useCallback(async (report: Omit<GeneratedReport, 'id' | 'generatedAt'>): Promise<GeneratedReport | null> => {
    try {
      console.log('📋 useSavedReports: Saving new report...', { type: report.type });
      const savedReport = await ReportsStorageService.saveReport(report);
      console.log('📋 useSavedReports: Report saved, updating state...', { id: savedReport.id });
      
      // Mise à jour immédiate du state
      setSavedReports(prev => {
        const newReports = [savedReport, ...prev];
        console.log('📋 useSavedReports: New state length:', newReports.length);
        return newReports;
      });
      
      return savedReport;
    } catch (err) {
      console.error('❌ useSavedReports: Error saving report:', err);
      setError('Erreur lors de la sauvegarde du rapport');
      return null;
    }
  }, []);

  // Supprimer un rapport
  const deleteReport = useCallback(async (id: string): Promise<boolean> => {
    try {
      await ReportsStorageService.deleteReport(id);
      setSavedReports(prev => prev.filter(report => report.id !== id));
      return true;
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Erreur lors de la suppression du rapport');
      return false;
    }
  }, []);

  // Récupérer les rapports par type
  const getReportsByType = async (type: FinancialStatementType): Promise<GeneratedReport[]> => {
    try {
      const reports = await ReportsStorageService.getReportsByType(type);
      return reports;
    } catch (err) {
      console.error('Error getting reports by type:', err);
      return [];
    }
  };

  // Supprimer tous les rapports
  const clearAllReports = async (): Promise<boolean> => {
    try {
      await ReportsStorageService.clearAllReports();
      setSavedReports([]);
      return true;
    } catch (err) {
      console.error('Error clearing all reports:', err);
      setError('Erreur lors de la suppression de tous les rapports');
      return false;
    }
  };

  // Charger les rapports au montage
  useEffect(() => {
    console.log('📋 useSavedReports: useEffect mounting, loading reports...');
    loadReports();
  }, [loadReports]);

  return {
    savedReports,
    loading,
    error,
    saveReport,
    deleteReport,
    getReportsByType,
    clearAllReports,
    refreshReports: loadReports
  };
}
